import React, { useCallback, useMemo } from 'react';

import { type AdminApproval, AdminApprovalType } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import {
  Button,
  DataTable,
  type DataTableColumnDef,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { Loading } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { Empty as TableEmpty, tableDefaults } from '~/components/Table';
import { useDeleteCustomRole, useListRoles } from '~/hooks/useRoles';
import {
  AdminApprovalReviewDialog,
  PendingApprovalStatus,
  ReviewChangesButton,
  useAdminApprovalReview,
  usePendingAdminApprovals,
} from '~/lib/approvals';
import { useNavigate } from '~/lib/navigation';
import { precannedRolesByName } from '~/repositories/RoleRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

import { ReasonTooltip } from './_partials/ReasonTooltip';
import { type RoleUser, RoleUsers } from './_partials/RoleUsers';

const MANAGE_ROLES_PERMISSION_TOOLTIP = 'You do not have permission to manage roles.';
const ASSIGNED_ROLE_DELETE_TOOLTIP =
  'This role is currently assigned to users. It cannot be deleted until it is unassigned.';

interface TableRow {
  roleId?: string;
  name: {
    name?: string;
    description?: string | null;
  };
  users: {
    count: number;
    list: RoleUser[];
  };
  editable?: boolean;
  pendingApproval?: AdminApproval;
}

const toRoleUser = ({ id, firstName, lastName }: { id: string; firstName: string; lastName: string }): RoleUser => ({
  id,
  name: `${firstName} ${lastName}`.trim(),
  initials: `${firstName.trim()[0] ?? ''}${lastName.trim()[0] ?? ''}` || '?',
});

export const PageRoles: React.FC = () => {
  const navigate = useNavigate();
  const { openAlert } = useDialog();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { isAuthorized } = useSessionStore();
  const canWriteRoles = isAuthorized({ permission: 'users', permissionLevel: 'write' });
  const handleCreateRole = useCallback(() => {
    navigate(ROUTE.PLATFORM_ROLES_CREATE);
  }, [navigate]);

  const { response: rolesResponse, isInitialLoad, isLoading, createRequest: fetchRoles } = useListRoles();
  const pendingRoleUpdates = usePendingAdminApprovals(AdminApprovalType.ROLE_UPDATE);
  const pendingByRole = useMemo(
    () => new Map(pendingRoleUpdates.map((approval) => [approval.request.role_update?.role_id, approval])),
    [pendingRoleUpdates]
  );
  const { openReview, getApprovalActions, startReview, dialogProps } = useAdminApprovalReview(pendingRoleUpdates);
  const deleteRole = useDeleteCustomRole({
    onSuccess: () => {
      addSuccessNotification({ content: 'Role deleted successfully' });
      fetchRoles();
    },
    onError: (error) => {
      addDangerNotification({ content: error.message });
    },
  });

  const roles: TableRow[] = useMemo(() => {
    return (rolesResponse?.roles || []).map((role) => ({
      roleId: role.id,
      name: {
        name: role.editable ? role.name : precannedRolesByName[role.name].label,
        description: role.description,
      },
      users: {
        count: role.userCount ?? 0,
        // The API previews the first few users on each role; userCount has the total.
        list: (role.userPreview ?? []).map(toRoleUser),
      },
      editable: role.editable,
      pendingApproval: pendingByRole.get(role.id),
    }));
  }, [rolesResponse, pendingByRole]);

  const handleDelete = useCallback(
    (row: TableRow) => {
      if (!row.roleId) {
        return;
      }
      const roleId = row.roleId;
      openAlert({
        headline: 'Delete role',
        content: 'Are you sure you want to delete this role? This action cannot be reversed.',
        confirmButton: {
          label: 'Delete Role',
          variant: 'danger',
        },
        onSuccess: () => {
          deleteRole.createRequest({ roleId });
        },
      });
    },
    [deleteRole, openAlert]
  );

  const columns: DataTableColumnDef<TableRow>[] = useMemo(
    () => [
      {
        header: 'Name',
        accessor: 'name',
        width: 'minmax(240px, 1fr)',
        className: 'min-w-0 whitespace-normal py-3',
        cell: ({ value }) => (
          <div className="flex max-w-[450px] min-w-0 flex-col items-start gap-0.5 [overflow-wrap:anywhere]">
            <span className="text-sm leading-5 font-medium text-foreground">{value.name ?? 'Unnamed'}</span>
            {value.description && <span className="text-xs leading-4 text-muted-foreground">{value.description}</span>}
          </div>
        ),
      },
      {
        header: '',
        accessor: 'pendingApproval',
        width: 'minmax(180px, 25%)',
        cell: ({ value }) =>
          value ? (
            <DataTable.Cell>
              <PendingApprovalStatus />
            </DataTable.Cell>
          ) : null,
      },
      {
        header: 'Users',
        accessor: 'users',
        width: 'minmax(min-content, 25%)',
        cell: ({ value }) => <RoleUsers count={value.count} users={value.list} />,
      },
      {
        header: '',
        accessor: 'editable',
        width: 'min-content',
        className: 'justify-end',
        cell: ({ row, value }) => {
          const deleteReason = !canWriteRoles
            ? MANAGE_ROLES_PERMISSION_TOOLTIP
            : row.users.count > 0
              ? ASSIGNED_ROLE_DELETE_TOOLTIP
              : undefined;
          return (
            // The menu is portaled, but React still bubbles its clicks through here to the row.
            <div className="flex justify-end" onClick={(event) => event.stopPropagation()}>
              {value ? (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="muted"
                      size="icon-xs"
                      aria-label="Role options"
                      className="group size-7 shadow-none"
                    >
                      <Icon.Dots className="transition-transform duration-200 [--icon-size:20px] group-data-[state=open]:rotate-90" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" sideOffset={4} className="min-w-48">
                    {(row.pendingApproval ? getApprovalActions(row.pendingApproval) : []).map((action) => (
                      <DropdownMenuItem key={action.label} onSelect={action.onClick}>
                        {action.label}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuItem onSelect={() => navigate(`${ROUTE.PLATFORM_ROLES}/${row.roleId}`)}>
                      {canWriteRoles ? 'Edit' : 'View'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {deleteReason ? (
                      // Disabled items ignore pointer events, so the tooltip hangs off a wrapper.
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div>
                              <DropdownMenuItem variant="destructive" disabled>
                                Delete
                              </DropdownMenuItem>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="max-w-xs">
                            {deleteReason}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <DropdownMenuItem variant="destructive" onSelect={() => handleDelete(row)}>
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <ReasonTooltip reason="Column-managed roles cannot be edited." side="left">
                  <Icon.Lock className="text-gray-400 dark:text-gray-500" />
                </ReasonTooltip>
              )}
            </div>
          );
        },
      },
    ],
    [canWriteRoles, getApprovalActions, handleDelete, navigate]
  );

  return (
    <>
      <AdminApprovalReviewDialog {...dialogProps} onReviewed={fetchRoles} />
      <PageActions>
        <ReviewChangesButton onClick={startReview} />
        <ReasonTooltip reason={canWriteRoles ? undefined : MANAGE_ROLES_PERMISSION_TOOLTIP} side="left">
          <Button type="button" size="sm" onClick={handleCreateRole} disabled={!canWriteRoles}>
            <Icon.Plus className="[--icon-size-x:16px] [--icon-size-y:16px]" />
            Create Role
          </Button>
        </ReasonTooltip>
      </PageActions>
      <Loading isLoading={isInitialLoad}>
        <div className="px-[var(--page-margin)] pb-6">
          <DataTable<TableRow>
            {...tableDefaults}
            columns={columns}
            data={roles}
            isLoading={isLoading}
            emptyContent={
              <TableEmpty
                icon={<Icon.AnimationLock />}
                title="No roles found"
                description="Create a custom role to get started."
                action={
                  <ReasonTooltip reason={canWriteRoles ? undefined : MANAGE_ROLES_PERMISSION_TOOLTIP}>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={handleCreateRole}
                      disabled={!canWriteRoles}
                    >
                      <Icon.Plus className="[--icon-size-x:16px] [--icon-size-y:16px]" />
                      Create Role
                    </Button>
                  </ReasonTooltip>
                }
              />
            }
            rowClassName={(row) =>
              row.pendingApproval ? 'group/row min-h-14' : row.editable ? 'min-h-14' : 'min-h-14 cursor-default'
            }
            rowKey={(row, index) => row.roleId ?? index}
            onRowClick={(row) => {
              if (row.pendingApproval) {
                openReview(row.pendingApproval);
              } else if (row.editable) {
                navigate(`${ROUTE.PLATFORM_ROLES}/${row.roleId}`);
              }
            }}
          />
        </div>
      </Loading>
    </>
  );
};
